import { useEffect, useMemo, useState } from "react";
import { api, ArtifactExpiredError } from "../api";
import { ExpiredPlaceholder } from "./ExpiredPlaceholder";

interface Props {
  jobId: string;
}

interface Segment {
  start?: number;
  end?: number;
  text: string;
}

type State =
  | { kind: "loading" }
  | { kind: "expired"; error: ArtifactExpiredError }
  | { kind: "error"; message: string }
  | { kind: "ready"; segments: Segment[] };

export function TranscriptTab({ jobId }: Props) {
  const [state, setState] = useState<State>({ kind: "loading" });
  const [filter, setFilter] = useState("");

  useEffect(() => {
    let cancelled = false;
    setState({ kind: "loading" });
    api
      .artifactTranscript(jobId)
      .then((view) => {
        if (cancelled) return;
        setState({ kind: "ready", segments: extractSegments(view.body) });
      })
      .catch((e) => {
        if (cancelled) return;
        if (e instanceof ArtifactExpiredError) {
          setState({ kind: "expired", error: e });
        } else {
          setState({
            kind: "error",
            message: e instanceof Error ? e.message : String(e),
          });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [jobId]);

  const filtered = useMemo(() => {
    if (state.kind !== "ready") return [];
    const q = filter.trim().toLowerCase();
    if (!q) return state.segments;
    return state.segments.filter((s) => s.text.toLowerCase().includes(q));
  }, [state, filter]);

  if (state.kind === "loading") return <p className="muted">Loading transcript…</p>;
  if (state.kind === "expired") return <ExpiredPlaceholder error={state.error} />;
  if (state.kind === "error") return <div className="error">{state.message}</div>;
  if (state.segments.length === 0)
    return <p className="muted">Transcript was empty.</p>;

  return (
    <div>
      <input
        type="text"
        placeholder="Filter transcript…"
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
        className="filter-input"
      />
      <ul className="transcript-list">
        {filtered.map((s, i) => (
          <li key={i}>
            {s.start !== undefined && (
              <code className="muted">{formatTs(s.start)}</code>
            )}
            <span style={{ marginLeft: 12 }}>{s.text}</span>
          </li>
        ))}
      </ul>
      {filtered.length === 0 && (
        <p className="muted">No segments match "{filter}".</p>
      )}
    </div>
  );
}

/// Best-effort segment extraction from the loose transcript shape. Whisper
/// writes `{ segments: [{start, end, text}] }`; some pipelines flatten to
/// just `{ text: "..." }`. Be permissive.
function extractSegments(body: unknown): Segment[] {
  if (!body || typeof body !== "object") return [];
  const obj = body as Record<string, unknown>;
  const segs = obj.segments;
  if (Array.isArray(segs)) {
    const out: Segment[] = [];
    for (const s of segs) {
      if (!s || typeof s !== "object") continue;
      const o = s as Record<string, unknown>;
      const text = typeof o.text === "string" ? o.text.trim() : "";
      if (!text) continue;
      const seg: Segment = { text };
      if (typeof o.start === "number") seg.start = o.start;
      if (typeof o.end === "number") seg.end = o.end;
      out.push(seg);
    }
    return out;
  }
  if (typeof obj.text === "string") {
    return [{ text: obj.text }];
  }
  return [];
}

function formatTs(seconds: number): string {
  const total = Math.max(0, Math.floor(seconds));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}
